import { useState } from "react";
import { ArrowLeft, Check } from "lucide-react";
import {
  MIN_QSE_BLACK,
  OPTION_CUSTOM,
  OPTION_FIFTY_ONE,
  OPTION_ZERO,
  isQse,
  pct,
  round2,
} from "@/data/ownership";

// Chosen after clicking an affidavit, before it opens (owner, 2026-10-10). The affidavit's % blanks,
// year-end and level tick are filled in from it. QSE affidavits: at least 51% black (no 0% option).
const PERCENT_RE = /^\d{1,3}(\.\d{1,2})?$/;

const parsePercent = (text) => {
  const t = text.trim().replace(",", ".");
  if (!PERCENT_RE.test(t)) return null;
  const v = Number(t);
  return v >= 0 && v <= 100 ? v : null;
};

const OptionCard = ({ title, badge, onClick, testId }) => (
  <button type="button" className="bee-comm-option bee-own-option" onClick={onClick} data-testid={testId}>
    <span className="bee-comm-option-name">{title}</span>
    {badge && <span className="bee-own-badge">{badge}</span>}
  </button>
);

const OwnershipModal = ({ slug, name, onDone, onCancel }) => {
  const qse = isQse(slug);
  const [step, setStep] = useState("choose");
  const [blackText, setBlackText] = useState("");
  const [femaleText, setFemaleText] = useState("0");

  const black = parsePercent(blackText);
  const female = parsePercent(femaleText);
  const blackProblem =
    blackText.trim() === "" ? null
      : black === null ? "Enter a percentage from 0 to 100 (at most 2 decimals)."
        : qse && black < MIN_QSE_BLACK
          ? `A QSE must be at least ${MIN_QSE_BLACK}% black owned to use this affidavit; otherwise a B-BBEE verification certificate is needed.`
          : null;
  const femaleProblem = femaleText.trim() !== "" && female === null ? "Enter a percentage from 0 to 100 (at most 2 decimals)." : null;
  const customOk = black !== null && !blackProblem && female !== null;
  const woman = customOk ? round2((black * female) / 100) : 0;

  const done = (o) => onDone(o);

  return (
    <div className="modal-overlay" data-testid="ownership-dialog" onClick={onCancel}>
      <div className="modal-content bee-own-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Black Ownership</h2>
        <p className="modal-description">
          <strong>{name}</strong>: choose the black ownership of the business. It is filled in on the affidavit
          {" "}(with the financial year-end and the B-BBEE level).
        </p>

        {step === "choose" && (
          <div className="bee-comm-options">
            {!qse && (
              <OptionCard
                title="0% Black Ownership / 100% White Ownership"
                badge="Default"
                onClick={() => done({ option: OPTION_ZERO, black: 0, woman: 0 })}
                testId="ownership-zero"
              />
            )}
            <OptionCard
              title="51% Black Ownership / 49% White Ownership"
              badge={qse ? "Default · Popular" : "Popular"}
              onClick={() => setStep("gender")}
              testId="ownership-51"
            />
            <OptionCard title="Custom Black Ownership percentages" onClick={() => setStep("custom")} testId="ownership-custom" />
            {qse && (
              <p className="bee-own-note">A QSE may only use an affidavit if it is at least {MIN_QSE_BLACK}% black owned.</p>
            )}
          </div>
        )}

        {step === "gender" && (
          <>
            <p className="bee-own-question">Is the 51% black owner male or female?</p>
            <div className="bee-own-pair">
              <button type="button" className="bee-comm-option bee-own-option" onClick={() => done({ option: OPTION_FIFTY_ONE, black: 51, woman: 0, gender: "male" })} data-testid="ownership-51-male">
                <span className="bee-comm-option-name">Male</span>
                <span className="bee-comm-option-title">0% black woman owned</span>
              </button>
              <button type="button" className="bee-comm-option bee-own-option" onClick={() => done({ option: OPTION_FIFTY_ONE, black: 51, woman: 51, gender: "female" })} data-testid="ownership-51-female">
                <span className="bee-comm-option-name">Female</span>
                <span className="bee-comm-option-title">51% black woman owned</span>
              </button>
            </div>
          </>
        )}

        {step === "custom" && (
          <form
            id="own-custom-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (customOk) done({ option: OPTION_CUSTOM, black, woman, femaleShare: female });
            }}
            noValidate
          >
            <label className="bee-label" htmlFor="own-black">Black ownership (% of the business)</label>
            <div className="bee-own-input">
              <input id="own-black" className="bee-input" inputMode="decimal" value={blackText} onChange={(e) => setBlackText(e.target.value)} autoFocus autoComplete="off" placeholder={qse ? `${MIN_QSE_BLACK} to 100` : "0 to 100"} data-testid="own-black" />
              <span>%</span>
            </div>
            {blackProblem ? (
              <p className="bee-field-hint is-error">{blackProblem}</p>
            ) : (
              <p className="bee-field-hint">White ownership: <strong>{black !== null ? `${pct(100 - black)}%` : "-"}</strong></p>
            )}

            <label className="bee-label" htmlFor="own-female">Of the black ownership, the part that is female (%)</label>
            <div className="bee-own-input">
              <input id="own-female" className="bee-input" inputMode="decimal" value={femaleText} onChange={(e) => setFemaleText(e.target.value)} autoComplete="off" placeholder="0 to 100" data-testid="own-female" />
              <span>%</span>
            </div>
            {femaleProblem ? (
              <p className="bee-field-hint is-error">{femaleProblem}</p>
            ) : (
              <p className="bee-field-hint">
                Male: <strong>{female !== null ? `${pct(100 - female)}%` : "-"}</strong> of the black ownership
                {customOk && <> &middot; Black woman owned: <strong>{pct(woman)}%</strong> of the business</>}
              </p>
            )}
          </form>
        )}

        <div className="modal-actions bee-own-actions">
          {step !== "choose" && (
            <button type="button" className="bee-btn-plain bee-inline" onClick={() => setStep("choose")}>
              <ArrowLeft size={16} /> Back
            </button>
          )}
          <button type="button" className="bee-btn-plain" onClick={onCancel}>Cancel</button>
          {step === "custom" && (
            <button type="submit" form="own-custom-form" className="btn-shimmer-orange bee-btn-done" disabled={!customOk} data-testid="own-continue">
              <span className="bee-inline"><Check size={16} /> Continue</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnershipModal;
